<script setup>
// NOTE: do not re-add Vue's `readonly` here. It is not used, and in <script setup>
// an import shadows the same-named prop in every template expression — so
// `v-if="!readonly"` compiled to `!<function>` (always false) and silently
// removed the tag input and the chip remove buttons from the DOM.
import { computed, nextTick, onMounted, onUnmounted, ref, reactive, watch } from 'vue';
import IconClose from './../components/icons/IconClose.vue';
import IconCaretDown from './../components/icons/IconCaretDown.vue';
import IconSearch from './../components/icons/IconSearch.vue';
import IconPendingUser from './../components/icons/IconPendingUser.vue';
import IconComponent from './icons/index.vue';

const props = defineProps({
  label: String,
  model: Array,
  options: Array,
  name: String,
  placeholder: {
    type: String,
    default: 'Search...',
  },
  errorMessage: {
    type: String,
    default: 'Field is required',
  },
  position: {
    type: String,
    default: 'auto',
    validator: (value) => ['auto', 'top', 'bottom'].includes(value),
  },
  max: Number,
  backgroundColor: String,
  info: String,
  disabled: Boolean,
  required: Boolean,
  focused: Boolean,
  readonly: Boolean,
  closeOnSelect: Boolean,

  // --- Creatable tag mode (opt-in) --------------------------------------
  // Off by default, and every branch below is gated on it, so existing call
  // sites (Agenda.vue's speaker pickers) render and behave exactly as before.
  //
  // Turns the field into a Jira-style tag combobox: the search input moves
  // inline next to the chips, a "Create …" row appears when the typed label
  // matches nothing, and Enter/comma commit.
  creatable: Boolean,
  // value -> { color, textColor }. Chip colours are supplied by the caller
  // rather than derived here so the same swatch can be reused on other
  // surfaces that render the same tags.
  chipColors: {
    type: Object,
    default: () => ({}),
  },
  // Shown instead of the option list when the caller has no options at all.
  emptyStateText: String,
  // Heading above the existing options in creatable mode.
  optionsHeading: {
    type: String,
    default: 'Existing tags',
  },
  // Marker on an option that is already selected.
  selectedHint: {
    type: String,
    default: 'already added',
  },
  createHint: {
    type: String,
    default: 'new tag',
  },
  // Caller-owned error message rendered under the field (e.g. the hub cap).
  errorText: String,
  // Returns an error string for an invalid label, or null/'' when it is fine.
  validateLabel: Function,
});

// `create` carries the raw typed label; the parent decides whether that becomes
// a pending chip, an API call, or is rejected.
const emit = defineEmits(['change', 'create']);

const containerEl = ref(null);
const inputEl = ref(null);
const optionsEl = ref(null);
const localModel = ref(props.model || []);
const isOpen = ref(false);
const searchModel = ref('');
const filteredOptions = ref(props.options);
const isBottomMenu = ref(true);
const isMenuOpened = ref(false);
const animationClasses = reactive({
  items: '',
  menu: 'fade-in-200',
});
const isFocused = computed(() => isMenuOpened.value);

// --- Creatable tag mode -------------------------------------------------
// Internal (component-raised) label error; kept apart from the caller's
// `errorText` so neither clobbers the other.
const localError = ref('');
const displayError = computed(() => localError.value || props.errorText || '');

const trimmedSearch = computed(() => (searchModel.value || '').trim());

const normalizeLabel = (value) => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

// Typing the label of an existing tag must SELECT it, not offer a duplicate —
// so the "Create" row only appears when nothing matches case-insensitively.
const exactMatch = computed(() => {
  if (!trimmedSearch.value) return null;
  const needle = normalizeLabel(trimmedSearch.value);
  return props.options?.find((option) => normalizeLabel(option.label) === needle) || null;
});

const canCreate = computed(() => !!trimmedSearch.value && !exactMatch.value);

// A brand-new hub has nothing to list; coach instead of showing an empty box.
const showEmptyState = computed(
  () => props.creatable && !!props.emptyStateText && !(props.options || []).length
);

const chipStyle = (value) => {
  const chip = props.chipColors?.[value];
  return chip ? { backgroundColor: chip.color, color: chip.textColor } : undefined;
};

const isSelected = (option) => localModel.value.includes(option.value);

const focusInput = () => {
  nextTick(() => inputEl.value?.focus());
};

// Enter / comma commit whatever is typed: an exact match selects, anything else
// is handed to the parent as a new tag. The input stays focused for the next one.
const commitTypedLabel = () => {
  const label = trimmedSearch.value;
  if (!label) return;

  if (exactMatch.value) {
    if (!isSelected(exactMatch.value)) handleSelect(exactMatch.value);
    searchModel.value = '';
    handleInputChange();
    localError.value = '';
    focusInput();
    return;
  }

  const error = props.validateLabel ? props.validateLabel(label) : '';
  if (error) {
    localError.value = error;
    return;
  }

  localError.value = '';
  emit('create', label);
  searchModel.value = '';
  handleInputChange();
  focusInput();
};

const handleTagKeydown = (event) => {
  if (event.key === 'Enter' || event.key === ',') {
    event.preventDefault();
    commitTypedLabel();
    return;
  }

  // Backspace on an empty input removes the last chip, the usual chip-input idiom.
  if (event.key === 'Backspace' && !searchModel.value && localModel.value.length) {
    event.preventDefault();
    handleRemoveItem(localModel.value.length - 1);
    return;
  }

  if (event.key === 'Escape') {
    animationClasses.menu = 'fade-out-200';
    isMenuOpened.value = false;
  }
};

// Clicking anywhere in the field opens the menu and puts the caret in the input.
const handleTagFieldClick = () => {
  if (props.readonly || props.disabled) return;
  if (!isOpen.value) {
    isOpen.value = true;
    isMenuOpened.value = true;
  }
  focusInput();
};

const handleSelect = (option) => {
  if (localModel.value.includes(option.value)) {
    localModel.value = localModel.value.filter(value => value !== option.value);
    emit('update:modelValue', localModel.value);
  } else {
    if (!props.max || localModel.value.length < props.max) {
      localModel.value.push(option.value);
    } else if (localModel.value.length === props.max) {
      // If the max number of items is reached, shake the dropdown items
      animationClasses.items = 'shake';
    }
  }
  if (props.closeOnSelect) {
    if (!props.max || localModel.value.length < props.max) {
      isOpen.value = false;
      isMenuOpened.value = false;
    }
  } else {
    // In creatable mode the search text is the field's own input; leaving it in
    // place after a pick would keep the list filtered to the tag just added.
    if (props.creatable) {
      searchModel.value = '';
      handleInputChange();
    }
    inputEl.value?.focus();
  }
  emit('change', localModel.value);
};

const handleToggleMenu = () => {
  if (props.readonly) return;
  if (!isOpen.value && localModel.value.length === props.max) {
    // If the max number of items is reached, shake the dropdown items
    animationClasses.items = 'shake';
    return;
  };
  if (!isOpen.value) {
    isOpen.value = true;
    isMenuOpened.value = true;
  } else {
    animationClasses.menu = 'fade-out-200';
    isMenuOpened.value = false;
  }
};

const handleInputChange = () => {
  // Plain substring match rather than `new RegExp(term)`: tag labels may contain
  // slashes and other characters that are legal here but would either throw or
  // silently mean something else when compiled as a pattern.
  const term = (searchModel.value || '').trim().toLowerCase();
  filteredOptions.value = term
    ? (props.options || []).filter(option => String(option.label ?? '').toLowerCase().includes(term))
    : (props.options || []);
};

const handleAnimationEnd = () => {
  const closing = animationClasses.menu === 'fade-out-200';

  // Focus only when the menu finished OPENING. `isOpen` is still true during the
  // close animation, so the original condition also grabbed focus on the way
  // out — pulling the caret back into a field the user had just left.
  //
  // The ref is optional on purpose: in creatable mode the input is not rendered
  // at all when the field is readonly/disabled, and an animationend can still
  // resolve after the subtree has gone (closing the Edit Member modal while the
  // menu is animating). Focus is a nicety here; a missing input is not an error.
  if (isOpen.value && !closing) {
    inputEl.value?.focus();
  }

  if (closing) {
    handleCloseMenu();
    animationClasses.menu = 'fade-in-200';
  }
};

const handleRemoveItem = (index) => {
  localModel.value.splice(index, 1);
  emit('change', localModel.value);
  // `disabled` as well as `readonly`: a disabled field has no input to focus and
  // no business reopening its menu.
  if (props.readonly || props.disabled) return;
  if (!isOpen.value) {
    isOpen.value = true;
    isMenuOpened.value = true;
  } else {
    inputEl.value?.focus();
  }
};

const handleCloseMenu = () => {
  isOpen.value = false;
  searchModel.value = '';
  filteredOptions.value = props.options;
};

const handleOutsideClick = (e) => {
  if (optionsEl.value) {
    if (!optionsEl.value.contains(e.target)) {
      if (isOpen.value) {
        animationClasses.menu = 'fade-out-200';
        isMenuOpened.value = false;
      }
    }
  }
};

const setMenuPlacement = (element) => {
  if (containerEl.value) {
    const { height: menuHeight } = optionsEl.value.getBoundingClientRect();
    const { top: inputTop, height: inputHeight } = containerEl.value.getBoundingClientRect();

    // If the element is the window, we get the inner height of the window
    // If not, we get the inner height of first the scrollable container
    const fullHeight = element === window ? window.innerHeight : element.offsetHeight;
    let spaceTop = inputTop;
    let spaceBottom = fullHeight - inputTop - inputHeight;

    // If the element is not the window, we calculate the space available
    if (element !== window) {
      spaceTop = containerEl.value.offsetTop - element.scrollTop - inputHeight;
      spaceBottom = element.offsetHeight - spaceTop - inputHeight;
    }

    if (menuHeight <= spaceBottom) {
      optionsEl.value.style.top = 'calc(100% + 8px)';
      isBottomMenu.value = true;
      return;
    }
    // If there is not enough space at the bottom but there is on top, set position on top
    if (menuHeight > spaceBottom && menuHeight <= spaceTop) {
      optionsEl.value.style.bottom = 'calc(100% + 8px)';
      isBottomMenu.value = false;
      return;
    }
    // If we pass both check, it means there is not enough space above and below the input
    // Position where there is more space available
    if (spaceBottom >= spaceTop) {
      optionsEl.value.style.top = 'calc(100% + 8px)';
      isBottomMenu.value = true;
      return;
    }
    optionsEl.value.style.bottom = 'calc(100% + 8px)';
    isBottomMenu.value = false;
    return;
  }
  optionsEl.value.style.top = 'calc(100% + 8px)';
  isBottomMenu.value = true;
};

const isScrollable = (el) => {
  if (el) {
    // Check if the element has scrollable content
    const hasScrollableContent = el.scrollHeight > el.clientHeight;

    const overflowYStyle = window.getComputedStyle(el).overflowY;
    // Check if the element has overflowY set to auto or scroll
    const hasOverflowY = overflowYStyle.indexOf('auto') !== -1 || overflowYStyle.indexOf('scroll') !== -1;

    return hasScrollableContent && hasOverflowY;
  }
  return true;
};

const getScrollableParent = (el) => {
  // If the element is the body or a document fragment, return the window
  if (!el || el === document.body || el.nodeType === Node.DOCUMENT_FRAGMENT_NODE) return window;
  // If the element is scrollable, return the element
  if (isScrollable(el)) return el;
  // If the element is not scrollable, check the parent
  return getScrollableParent(el.parentNode);
};

watch(() => props.model, (newValue) => {
  localModel.value = newValue;
});

// `filteredOptions` is a snapshot taken on search/close, so a parent that appends
// an option while the menu is open (creatable mode does exactly that) would
// otherwise not see it until the next keystroke.
watch(() => props.options, () => {
  handleInputChange();
});

// A fresh keystroke clears a stale "that name is invalid" message.
watch(searchModel, () => {
  if (localError.value) localError.value = '';
});

watch(optionsEl, (newValue) => {
  if (newValue) {
    const scrollParent = getScrollableParent(containerEl.value.parentElement);
    // Set the position of the menu, based on the scrollable parent
    setMenuPlacement(scrollParent);
  }
}, {
  // Before the browser repaints the screen, we update the position of the menu
  flush: 'post',
});

const getItemLabel = (value) => computed(() => {
  const option = (props.options || []).find(option => option.value === value);
  if (option) return option.label;

  // Integers are ids, so a miss means the option list hasn't loaded — render
  // nothing rather than the bare number. Anything else IS the label.
  return Number.isInteger(value) ? '' : value;
});

const getItemImage = (value) => computed(() => {
  // Same guard as getItemLabel: `options` can be undefined before the caller's
  // fetch resolves, and chips render from `model` independently of it.
  const option = (props.options || []).find(option => option.value === value);
  return option ? option.img : '';
});

onMounted(() => {
  window.addEventListener('click', handleOutsideClick);
});

onUnmounted(() => {
  window.removeEventListener('click', handleOutsideClick);
});
</script>

<template>
  <div ref="containerEl" class="s-container">
    <div
      class="s-container__content"
      :style="{ backgroundColor: backgroundColor }"
    >
      <label :class="disabled ? 'disabled' : undefined">
        <span
          v-if="label"
          :class="[
            isFocused ? 'focused' : undefined,
          ]"
        >
          {{ label }}
          <span v-if="required">*</span>
        </span>
    
        <!--
          Creatable tag mode: the search input lives inline with the chips, so the
          field reads as one combobox. Structurally separate from the default
          branch below rather than conditionals sprinkled through it, because the
          two have different interaction models (type-to-create vs pick-from-list).
        -->
        <div
          v-if="creatable"
          class="input-container input-container--tags"
          :class="[
            isFocused ? 'focused' : undefined,
            disabled ? 'disabled' : undefined,
            displayError ? 'has-error' : undefined,
          ]"
          @click.stop="handleTagFieldClick"
        >
          <div class="tag-field">
            <div
              v-for="(item, index) in localModel"
              :key="index"
              class="tag-chip"
              :style="chipStyle(item)"
            >
              <span class="tag-chip__label">{{ getItemLabel(item).value }}</span>
              <button
                v-if="!props.readonly && !props.disabled"
                type="button"
                class="tag-chip__remove"
                :aria-label="`Remove ${getItemLabel(item).value}`"
                @click.stop="handleRemoveItem(index)"
              >
                <icon-close size="14" />
              </button>
            </div>

            <input
              v-if="!props.readonly && !props.disabled"
              ref="inputEl"
              type="text"
              class="tag-field__input"
              :placeholder="localModel.length ? '' : placeholder"
              v-model="searchModel"
              @input="handleInputChange"
              @keydown="handleTagKeydown"
              @click.stop="handleTagFieldClick"
            />
          </div>
        </div>

        <div
          v-else
          role="button"
          :aria-labelledby="isOpen ? 'Close Options' : 'Open Options'"
          class="input-container"
          :class="[
            isFocused ? 'focused' : undefined,
            disabled ? 'disabled' : undefined,
          ]"
          @click.stop="handleToggleMenu"
        >
          <div class="item-container">
            <div
              class="item-container__selected"
              :class="animationClasses.items"
              @animationend="animationClasses.items = ''"
            >
              <div
                v-for="(item, index) in localModel"
                :key="index"
                class="item-container__selected__item"
              >
                <span v-if="getItemImage(item).value" class="item-container__selected__item__img">
                  <icon-component v-if="getItemImage(item).value === 'Default'" icon="DefaultAvatar" />
                  <img v-else width="24" height="24" :src="getItemImage(item).value" :alt="getItemLabel(item).value" />
                </span>
                <span v-else class="item-container__selected__item__img">
                  <icon-pending-user size="24" />
                </span>
                <span class="item-container__selected__item__title">{{ getItemLabel(item).value }}</span>
                <span class="icon-close">
                  <icon-close size="18" @click.stop="handleRemoveItem(index)" />
                </span>
              </div>
            </div>
          </div>
  
          <span v-if="props.max" class="max-info">
            {{ localModel.length }} / {{ props.max }}
          </span>
    
          <span
            v-if="!props.readonly"
            role="button"
            :aria-labelledby="isOpen ? 'Close Options' : 'Open Options'"
            class="pge-icon caret"
            @click.stop="handleToggleMenu"
          >
            <icon-caret-down :class="isMenuOpened ? 'up' : 'down'" />
          </span>
        </div>
      </label>

      <span v-if="creatable && displayError" class="tag-error">{{ displayError }}</span>
      <span v-if="info" class="info">{{ info }}</span>
    </div>

    <!-- Creatable tag menu: create row, then the hub's existing tags. -->
    <div
      v-if="isOpen && creatable"
      ref="optionsEl"
      class="s-container__menu s-container__menu--tags"
      :class="[isBottomMenu ? 'bottom' : 'top', animationClasses.menu]"
      @animationend="handleAnimationEnd"
    >
      <div class="s-container__menu__content">
        <!--
          Brand-new hub: no tags exist yet, so coach rather than render an empty
          list. One primary action only, per spec — Create.
        -->
        <div v-if="showEmptyState" class="tag-empty-state">
          <p class="tag-empty-state__text">{{ emptyStateText }}</p>
          <button
            type="button"
            class="tag-create-row tag-create-row--primary"
            @click.stop="canCreate ? commitTypedLabel() : focusInput()"
          >
            <span class="tag-create-row__plus">+</span>
            <span class="tag-create-row__label">
              {{ canCreate ? `Create "${trimmedSearch}"` : 'Create' }}
            </span>
          </button>
        </div>

        <template v-else>
          <button
            v-if="canCreate"
            type="button"
            class="tag-create-row"
            @click.stop="commitTypedLabel"
          >
            <span class="tag-create-row__plus">+</span>
            <span class="tag-create-row__label">Create "{{ trimmedSearch }}"</span>
            <span class="tag-create-row__hint">{{ createHint }}</span>
          </button>

          <div v-if="filteredOptions.length" class="tag-options">
            <div class="tag-options__heading">{{ optionsHeading }}</div>
            <div
              v-for="(option, index) in filteredOptions"
              :key="index"
              class="tag-options__row"
              :class="{ 'tag-options__row--selected': isSelected(option) }"
              role="button"
              @click.stop="isSelected(option) ? null : handleSelect(option)"
            >
              <span class="tag-chip" :style="chipStyle(option.value)">
                <span class="tag-chip__label">{{ option.label }}</span>
              </span>
              <span v-if="isSelected(option)" class="tag-options__hint">{{ selectedHint }}</span>
            </div>
          </div>

          <div v-else-if="!canCreate" class="tag-options__empty">No matching tags</div>
        </template>
      </div>
    </div>

    <div
      v-else-if="isOpen"
      ref="optionsEl"
      class="s-container__menu"
      :class="[isBottomMenu ? 'bottom' : 'top', animationClasses.menu]"
      @animationend="handleAnimationEnd"
    >
      <div class="s-container__menu__content">
        <div
          v-if="isBottomMenu"
          class="s-container__menu__content__list search-box top"
        >
          <div class="search-box__content">
            <input
              ref="inputEl"
              type="text"
              v-model="searchModel"
              :placeholder="placeholder"
              aria-label="Filter the options"
              @input="handleInputChange"
            />
            <span>
              <icon-search />
            </span>
          </div>
        </div>

        <div class="s-container__menu__content__options">
          <div
            v-for="(option, index) in filteredOptions"
            :key="index"
            role="button"
            :aria-labelledby="localModel.includes(option.value) ? 'Select Option' : 'Discard Option'"
            class="s-container__menu__content__options__list"
            :class="{ 'selected': localModel.includes(option.value) }"
            @click="handleSelect(option)"
          >
            <span v-if="option.img" class="s-container__menu__content__options__list__img">
              <icon-component v-if="option.img === 'Default'" icon="DefaultAvatar" />
              <img v-else width="24" height="24" :src="option.img" :alt="option.label" />
            </span>
            <span
              class="s-container__menu__content__options__list__item truncate"
              :style="{ maxWidth: option.info ? '50%' : '100%' }"
            >
              {{ option.label }}
            </span>
            <span
              v-if="option.info"
              class="s-container__menu__content__options__list__info"
            >
              {{ option.info }}
            </span>
          </div>
        </div>

        <div
          v-if="!isBottomMenu"
          class="s-container__menu__content__list search-box bottom"
        >
          <div class="search-box__content">
            <input
              ref="inputEl"
              type="text"
              v-model="searchModel"
              :placeholder="placeholder"
              aria-label="Filter the options"
              @input="handleInputChange"
            />
            <span>
              <icon-search />
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url("./styles/pge-multiselect/index.scss");
</style>
